/**
 * Asset Tag Parser
 *
 * Parses and validates <asset .../> tags from streamed content,
 * replacing valid tags with span elements that the React component
 * handler can detect and render as AssetChart components.
 */

import { normalizeDate } from './market-data-utils';

const VALID_ASSET_TYPES = ['stock', 'crypto', 'commodity', 'forex', 'index'];
const STANDARD_INTERVALS = ['1d', '1h', '5m'];
const INDEX_INTERVALS = [...STANDARD_INTERVALS, '1m', '15m', '30m', '4h'];
const VALID_CHART_TYPES = ['candlestick', 'line'];

function isValidInterval(assetType: string, interval: string): boolean {
  return (assetType === 'index' ? INDEX_INTERVALS : STANDARD_INTERVALS).includes(interval);
}

export interface ParsedAssetTag {
  symbol: string;
  assetType: string;
  interval: string;
  chartType: string;
  start?: string;
  end?: string;
  studies?: string;
}

/**
 * Parse and validate a single <asset .../> tag match string.
 * Returns null if required attributes are missing or invalid.
 */
export function parseAssetTag(match: string): ParsedAssetTag | null {
  const symbolMatch = match.match(/symbol="([^"]*)"/);
  const assetTypeMatch = match.match(/asset_type="([^"]*)"/);
  const intervalMatch = match.match(/interval="([^"]*)"/);
  const chartTypeMatch = match.match(/chart_type="([^"]*)"/);
  const startMatch = match.match(/start="([^"]*)"/);
  const endMatch = match.match(/end="([^"]*)"/);
  const studiesMatch = match.match(/studies="([^"]*)"/);

  // Required: symbol
  const symbol = symbolMatch?.[1];
  if (!symbol) return null;

  // Required: asset_type (must be valid)
  const assetType = assetTypeMatch?.[1];
  if (!assetType || !VALID_ASSET_TYPES.includes(assetType)) return null;

  // Optional with defaults
  const rawInterval = intervalMatch?.[1];
  const interval = rawInterval && isValidInterval(assetType, rawInterval) ? rawInterval : '1d';

  const rawChartType = chartTypeMatch?.[1];
  const chartType = rawChartType && VALID_CHART_TYPES.includes(rawChartType) ? rawChartType : 'candlestick';

  // Optional dates: normalize (accepts YYYY-MM-DD and ISO datetime with fallback)
  let start = startMatch?.[1] ? normalizeDate(startMatch[1]) ?? undefined : undefined;
  let end = endMatch?.[1] ? normalizeDate(endMatch[1]) ?? undefined : undefined;

  // Swap if start > end
  if (start && end && start > end) {
    console.warn(`[asset-tag-parser] start "${start}" > end "${end}", swapping`);
    [start, end] = [end, start];
  }

  const studies = studiesMatch?.[1] || undefined;

  return { symbol, assetType, interval, chartType, start, end, studies };
}

/**
 * Preprocess content to replace valid <asset .../> tags with
 * <span data-asset="true" ...></span> elements.
 * Invalid tags are left as raw text.
 * Tags inside fenced code blocks are preserved as-is.
 */
export function preprocessAssetTags(content: string): string {
  const ASSET_TAG_REGEX = /<asset\s+[^>]*\/>/g;

  // Split on fenced code block boundaries to avoid replacing tags inside code
  const CODE_FENCE_REGEX = /(```[\s\S]*?```)/g;
  const parts = content.split(CODE_FENCE_REGEX);

  return parts.map((part, i) => {
    // Odd-indexed parts are code fence matches — leave untouched
    if (i % 2 === 1) return part;

    return part.replace(ASSET_TAG_REGEX, (match) => {
      const parsed = parseAssetTag(match);
      if (!parsed) return match;

      let attrs = `data-asset="true" data-asset-symbol="${parsed.symbol}" data-asset-type="${parsed.assetType}"`;
      attrs += ` data-asset-interval="${parsed.interval}" data-asset-chart-type="${parsed.chartType}"`;
      if (parsed.start) attrs += ` data-asset-start="${parsed.start}"`;
      if (parsed.end) attrs += ` data-asset-end="${parsed.end}"`;
      if (parsed.studies) attrs += ` data-asset-studies="${parsed.studies}"`;

      return `<span ${attrs}></span>`;
    });
  }).join('');
}
